import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getAnalyticsConsent, setAnalyticsConsent } from '../firebase/analytics';
import SiteFooter from '../components/ui/SiteFooter';
import './analytics.css';

/** A compact bottom banner; the footer keeps consent preferences available. */
export default function AnalyticsControls({ children }) {
  const [consent, setConsent] = useState(getAnalyticsConsent);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [status, setStatus] = useState('');
  const preferencesButton = useRef(null);
  const preferencesHeading = useRef(null);
  const banner = useRef(null);
  const bannerSpace = useRef(null);
  const showChoices = consent === null || preferencesOpen;

  useEffect(() => {
    if (!showChoices) return;
    const root = document.documentElement;
    const previousScrollPadding = root.style.scrollPaddingBottom;
    const reserveSpace = () => {
      const height = banner.current.getBoundingClientRect().height;
      bannerSpace.current.style.height = `${height}px`;
      root.style.scrollPaddingBottom = `${height}px`;
    };
    reserveSpace();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(reserveSpace);
    observer?.observe(banner.current);
    window.addEventListener('resize', reserveSpace);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', reserveSpace);
      root.style.scrollPaddingBottom = previousScrollPadding;
    };
  }, [showChoices]);

  useEffect(() => {
    const updateConsent = () => {
      const choice = getAnalyticsConsent();
      setConsent(choice);
      setStatus(choice === null ? '' : choice === 'granted' ? 'Optional cookies accepted.' : 'Optional cookies declined.');
    };
    window.addEventListener('analytics-consent-change', updateConsent);
    return () => window.removeEventListener('analytics-consent-change', updateConsent);
  }, []);

  useEffect(() => {
    if (preferencesOpen) preferencesHeading.current?.focus();
  }, [preferencesOpen]);

  const closePreferences = () => {
    setPreferencesOpen(false);
    if (preferencesOpen) {
      preferencesButton.current?.focus();
    } else {
      document.getElementById('main')?.focus({ preventScroll: true });
    }
  };

  const chooseConsent = (choice) => {
    setAnalyticsConsent(choice);
    setConsent(getAnalyticsConsent());
    setStatus(choice === 'granted' ? 'Optional cookies accepted.' : 'Optional cookies declined.');
    closePreferences();
  };

  return (
    <>
      {children}
      <SiteFooter
        preferencesButtonRef={preferencesButton}
        showChoices={showChoices}
        onOpenPreferences={() => setPreferencesOpen(true)}
        status={status}
        consent={consent}
      />
      {showChoices ? (
        <>
          <div ref={bannerSpace} aria-hidden="true" />
          <section id="analytics-preferences" className="kds-analytics-banner" ref={banner} aria-labelledby="analytics-preferences-heading">
            <div className="kds-analytics-banner-inner">
              <div>
                <h2 id="analytics-preferences-heading" className="kds-title-sm" ref={preferencesHeading} tabIndex={-1}>
                  Cookies on this site
                </h2>
                <p className="kds-body-sm kds-muted">
                  Optional cookies help us understand the website visitors.{' '}
                  <Link to="/privacy" aria-label="Read the website privacy notice">Privacy</Link>.
                </p>
                {consent !== null ? (
                  <p className="kds-body-sm kds-muted">
                    Current choice: optional cookies {consent === 'granted' ? 'accepted' : 'declined'}.
                  </p>
                ) : null}
              </div>
              <div className="kds-cluster kds-analytics-actions">
                <button type="button" className="kds-btn kds-btn--tonal kds-btn--sm" onClick={() => chooseConsent('granted')}>
                  Accept cookies
                </button>
                <button type="button" className="kds-btn kds-btn--tonal kds-btn--sm" onClick={() => chooseConsent('denied')}>
                  Decline
                </button>
                {consent !== null ? (
                  <button type="button" className="kds-btn kds-btn--text kds-btn--sm" onClick={closePreferences}>
                    Keep current choice
                  </button>
                ) : null}
              </div>
            </div>
          </section>
        </>
      ) : null}
    </>
  );
}
